import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { restaurantsAPI, modulesAPI, restaurantTasksAPI, usersAPI, uploadFile, deleteAttachment } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Building, Mail, Phone, MapPin, Pencil, Check, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentGallery, fileAuthUrl } from '../components/AttachmentGallery';
import { CommentsSection } from '../components/CommentsSection';
import RestaurantSystems from '../components/RestaurantSystems';
import RestaurantLinks from '../components/RestaurantLinks';
import RestaurantFormModal from '../components/RestaurantFormModal';
import RestaurantTaskModal from '../components/RestaurantTaskModal';
import { RestaurantTaskFilters, RestaurantTaskBoard, filterRestaurantTasks } from '../components/RestaurantTaskViews';
import { RESTAURANT_STATUS_MAP } from '../lib/restaurantConstants';
import { formatCurrency, formatDate } from '../lib/format';
import { canManageRestaurants } from '../lib/roles';

function ModulesCard({ restaurant, allModules, canEdit, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [priceDraft, setPriceDraft] = useState(0);
  const [form, setForm] = useState({
    module_id: '',
    monthly_price: 0,
    activation_date: new Date().toISOString().split('T')[0],
    create_integration_task: false,
  });

  const used = restaurant.modules || [];
  const available = allModules.filter(m => !used.some(rm => rm.id === m.id));
  const totalMrr = used.reduce((s, m) => s + (m.monthly_price || 0), 0);

  const handleAdd = async () => {
    if (!form.module_id) { toast.error('Please select a module'); return; }
    try {
      await restaurantsAPI.addModule(restaurant.id, form);
      toast.success('Module added');
      setShowAdd(false);
      setForm({ module_id: '', monthly_price: 0, activation_date: new Date().toISOString().split('T')[0], create_integration_task: false });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add module');
    }
  };

  const handleRemove = async (moduleId) => {
    if (!window.confirm('Remove this module?')) return;
    try {
      await restaurantsAPI.removeModule(restaurant.id, moduleId);
      toast.success('Module removed');
      onChanged();
    } catch (err) {
      toast.error('Failed to remove module');
    }
  };

  const savePrice = async (moduleId) => {
    try {
      await restaurantsAPI.updateModule(restaurant.id, moduleId, { monthly_price: parseFloat(priceDraft) || 0 });
      toast.success('Price updated');
      setEditingId(null);
      onChanged();
    } catch (err) {
      toast.error('Failed to update price');
    }
  };

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden" data-testid="restaurant-modules-card">
      <div className="p-4 border-b border-[#E0E0E0] flex items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-[#111111]">Modules in use</h3>
          <p className="text-xs text-[#666666]">Prices default to each module&apos;s list price</p>
        </div>
        {canEdit && available.length > 0 && (
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="add-module-btn">
            <Plus size={14} className="mr-1" /> Add Module
          </Button>
        )}
      </div>

      {used.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="restaurant-modules-table">
            <thead className="bg-[#F5F5F5]">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Module</th>
                <th className="text-right p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Monthly price</th>
                <th className="text-left p-3 text-xs font-medium text-[#666666] uppercase tracking-wider">Activation</th>
                {canEdit && <th className="p-3 w-20"></th>}
              </tr>
            </thead>
            <tbody>
              {used.map(mod => (
                <tr key={mod.id} className="border-t border-[#E0E0E0]" data-testid={`restaurant-module-row-${mod.id}`}>
                  <td className="p-3 font-medium text-sm">{mod.name}</td>
                  <td className="p-3 text-right">
                    {editingId === mod.id ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Input type="number" value={priceDraft} onChange={e => setPriceDraft(e.target.value)} className="h-8 w-24 text-right" data-testid={`module-price-input-${mod.id}`} />
                        <button onClick={() => savePrice(mod.id)} className="text-[#059669]" data-testid={`module-price-save-${mod.id}`}><Check size={16} /></button>
                      </div>
                    ) : (
                      <button
                        className="text-sm font-semibold hover:text-[#FF6B35] disabled:hover:text-inherit"
                        disabled={!canEdit}
                        onClick={() => { setEditingId(mod.id); setPriceDraft(mod.monthly_price); }}
                        data-testid={`module-price-${mod.id}`}
                      >
                        {formatCurrency(mod.monthly_price)}{canEdit && <Pencil size={11} className="inline ml-1 text-[#C0C0C0]" />}
                      </button>
                    )}
                  </td>
                  <td className="p-3 text-sm text-[#666666]">{mod.activation_date || '—'}</td>
                  {canEdit && (
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleRemove(mod.id)} data-testid={`remove-module-${mod.id}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#FAFAFA]">
              <tr className="border-t-2 border-[#E0E0E0]">
                <td className="p-3 font-bold text-sm">Total MRR</td>
                <td className="p-3 text-right font-bold text-sm text-[#059669]" data-testid="restaurant-modules-total">{formatCurrency(totalMrr)}</td>
                <td className="p-3 text-xs text-[#666666]">{formatCurrency(totalMrr * 12)}/yr</td>
                {canEdit && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="p-6 text-center text-[#666666] text-sm">No modules assigned</div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-module-dialog">
          <DialogHeader><DialogTitle>Add module to {restaurant.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Module</Label>
              <Select
                value={form.module_id || undefined}
                onValueChange={(v) => {
                  const mod = allModules.find(m => m.id === v);
                  setForm(f => ({ ...f, module_id: v, monthly_price: mod?.price || 0 }));
                }}
              >
                <SelectTrigger data-testid="select-module"><SelectValue placeholder="Select module" /></SelectTrigger>
                <SelectContent>
                  {available.map(m => <SelectItem key={m.id} value={m.id}>{m.name} (${m.price}/mo)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monthly price (USD)</Label>
              <Input type="number" value={form.monthly_price} onChange={e => setForm(f => ({ ...f, monthly_price: parseFloat(e.target.value) || 0 }))} data-testid="module-price" />
            </div>
            <div>
              <Label>Activation date</Label>
              <Input type="date" value={form.activation_date} onChange={e => setForm(f => ({ ...f, activation_date: e.target.value }))} data-testid="activation-date" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="create-task" checked={form.create_integration_task} onCheckedChange={(c) => setForm(f => ({ ...f, create_integration_task: c }))} />
              <Label htmlFor="create-task" className="text-sm cursor-pointer">Schedule integration task on roadmap</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="confirm-add-module">Add Module</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RestaurantLogo({ restaurant, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const src = restaurant.logo_path ? fileAuthUrl(restaurant.logo_path) : (restaurant.logo_url || '');

  const handleUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await restaurantsAPI.uploadLogo(restaurant.id, file);
      toast.success('Logo updated');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await restaurantsAPI.deleteLogo(restaurant.id);
      toast.success('Logo removed');
      onChanged();
    } catch (err) {
      toast.error('Failed to remove logo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative group w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0" data-testid="restaurant-logo">
      {src ? (
        <img src={src} alt={restaurant.name} className="w-full h-full rounded-lg object-contain bg-white border border-[#E0E0E0] p-1" />
      ) : (
        <div className="w-full h-full rounded-lg bg-[#CFFF40] flex items-center justify-center">
          <Building size={28} className="text-[#101010]" />
        </div>
      )}
      {canEdit && (
        <>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="absolute inset-0 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center"
            title="Upload restaurant logo for the PDF proposal"
            data-testid="upload-logo-btn"
          >
            <ImagePlus size={16} />
            <span className="text-[9px] mt-0.5">{busy ? '...' : 'Logo'}</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="restaurant-logo-input"
          />
          {src && (
            <button
              onClick={handleRemove}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-[#E0E0E0] text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              title="Remove logo"
              data-testid="remove-logo-btn"
            >
              <X size={11} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function GeneralInfoCard({ restaurant, canEdit, onEdit }) {
  const status = RESTAURANT_STATUS_MAP[restaurant.status] || RESTAURANT_STATUS_MAP.backlog;
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg p-4 sm:p-6" data-testid="restaurant-general-card">
      <div className="flex items-start justify-between gap-2 mb-4">
        <h3 className="font-semibold text-[#111111]">General information</h3>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-restaurant-btn">
            <Pencil size={13} className="mr-1" /> Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2 text-sm">
          <p className="flex items-center gap-2"><MapPin size={14} className="text-[#666666]" /> {restaurant.location || '—'}</p>
          <div className="flex items-center gap-2"><span className="text-[#666666]">Status:</span> <Badge className={status.color}>{status.label}</Badge></div>
          <p><span className="text-[#666666]">Cuisine:</span> {restaurant.cuisine_type || '—'}</p>
          <p><span className="text-[#666666]">Contract:</span> {restaurant.contract_start ? `${formatDate(restaurant.contract_start)} — ${formatDate(restaurant.contract_end)}` : '—'}</p>
          <p><span className="text-[#666666]">Trial:</span> {restaurant.trial_start ? `${formatDate(restaurant.trial_start)} — ${formatDate(restaurant.trial_end)}` : '—'}</p>
        </div>
        <div className="space-y-2 text-sm">
          <p className="font-medium">{restaurant.contact_name || 'No contact'}{restaurant.contact_title ? ` — ${restaurant.contact_title}` : ''}</p>
          <p className="flex items-center gap-2"><Mail size={14} className="text-[#666666]" /> {restaurant.contact_email || '—'}</p>
          <p className="flex items-center gap-2"><Phone size={14} className="text-[#666666]" /> {restaurant.contact_phone || '—'}</p>
          <p><span className="text-[#666666]">Modules:</span> {restaurant.modules?.length || 0}</p>
          <p><span className="text-[#666666]">MRR:</span> <span className="font-bold text-[#059669]" data-testid="restaurant-detail-mrr">{formatCurrency(restaurant.mrr)}</span> · ARR {formatCurrency(restaurant.arr)}</p>
        </div>
      </div>
    </div>
  );
}

function AttachmentsCard({ restaurant, canEdit, onChanged }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        await uploadFile(f, null, null, null, restaurant.id);
      }
      toast.success('Files uploaded');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteAttachment(id);
      toast.success('File deleted');
      onChanged();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg p-4" data-testid="restaurant-attachments-card">
      <h3 className="font-semibold text-[#111111] mb-3">Attachments</h3>
      {(restaurant.attachments || []).length === 0 && <p className="text-sm text-[#999999]">No files yet.</p>}
      <AttachmentGallery attachments={restaurant.attachments || []} canDelete={canEdit} onDelete={handleDelete} />
      {canEdit && (
        <Input
          type="file"
          multiple
          disabled={uploading}
          onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
          className="mt-3"
          data-testid="restaurant-file-input"
        />
      )}
    </div>
  );
}

export default function RestaurantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = canManageRestaurants(user);

  const [restaurant, setRestaurant] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [taskModal, setTaskModal] = useState(undefined);
  const [filterType, setFilterType] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [rRes, mRes, tRes, uRes] = await Promise.all([
        restaurantsAPI.getOne(id),
        modulesAPI.getAll(),
        restaurantTasksAPI.getAll({ restaurant_id: id }),
        usersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setRestaurant(rRes.data);
      setAllModules(mRes.data);
      setTasks(tRes.data);
      setUsers(uRes.data);
    } catch (err) {
      toast.error('Failed to load restaurant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const visibleTasks = useMemo(
    () => filterRestaurantTasks(tasks, { filterType, filterAssignee, selectedRestaurants: [] }),
    [tasks, filterType, filterAssignee]
  );

  const handleTaskStatusChange = async (task, status) => {
    try {
      await restaurantTasksAPI.update(task.id, { status });
      toast.success('Task moved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move task');
    }
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Restaurant">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!restaurant) {
    return (
      <DashboardLayout pageTitle="Restaurant Not Found">
        <div className="text-center py-12">
          <p className="text-[#666666]">Restaurant not found</p>
          <Button onClick={() => navigate('/restaurants')} className="mt-4">Back to Restaurants</Button>
        </div>
      </DashboardLayout>
    );
  }

  const status = RESTAURANT_STATUS_MAP[restaurant.status] || RESTAURANT_STATUS_MAP.backlog;

  return (
    <DashboardLayout pageTitle={restaurant.name}>
      <div className="space-y-6" data-testid="restaurant-detail-page">
        <Button variant="ghost" onClick={() => navigate('/restaurants')} data-testid="back-to-restaurants">
          <ArrowLeft size={16} className="mr-2" /> Back to Restaurants
        </Button>

        {/* Header */}
        <div className="bg-white border border-[#E0E0E0] rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <RestaurantLogo restaurant={restaurant} canEdit={canEdit} onChanged={fetchData} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-[#111111]">{restaurant.name}</h1>
                <Badge className={status.color}>{status.label}</Badge>
              </div>
              <p className="text-[#666666] text-sm">{restaurant.location || 'No location'}{restaurant.cuisine_type ? ` • ${restaurant.cuisine_type}` : ''}</p>
            </div>
            <div className="sm:text-right">
              <p className="text-lg font-bold text-[#059669]">{formatCurrency(restaurant.mrr)}/mo</p>
              <p className="text-sm text-[#666666]">{restaurant.modules?.length || 0} modules · {tasks.length} tasks</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="bg-white border border-[#E0E0E0]">
            <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-restaurant-tasks">Tasks</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-4 space-y-4">
            <GeneralInfoCard restaurant={restaurant} canEdit={canEdit} onEdit={() => setShowEdit(true)} />
            <ModulesCard restaurant={restaurant} allModules={allModules} canEdit={canEdit} onChanged={fetchData} />
            <RestaurantSystems
              key={`systems-${restaurant.systems?.length || 0}`}
              restaurantId={restaurant.id}
              restaurantName={restaurant.name}
              systems={restaurant.systems || []}
              modules={allModules}
              hasModules={(restaurant.modules || []).length > 0}
              canEdit={canEdit}
              onSaved={fetchData}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <AttachmentsCard restaurant={restaurant} canEdit={canEdit} onChanged={fetchData} />
              <RestaurantLinks restaurantId={restaurant.id} links={restaurant.links || []} canEdit={canEdit} onSaved={fetchData} />
            </div>
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-4" data-testid="restaurant-notes-card">
              <CommentsSection
                entityId={restaurant.id}
                getComments={restaurantsAPI.getComments}
                addComment={restaurantsAPI.addComment}
                currentUser={user}
                label="Notes"
                placeholder="Add a note about this restaurant..."
              />
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4 space-y-4">
            <RestaurantTaskFilters
              filterType={filterType} setFilterType={setFilterType}
              filterAssignee={filterAssignee} setFilterAssignee={setFilterAssignee}
              selectedRestaurants={[]} setSelectedRestaurants={() => {}}
              users={users}
              count={visibleTasks.length}
              onNewTask={canEdit ? () => setTaskModal(null) : undefined}
            />
            <RestaurantTaskBoard
              tasks={visibleTasks}
              showRestaurant={false}
              onTaskClick={setTaskModal}
              onStatusChange={handleTaskStatusChange}
            />
          </TabsContent>
        </Tabs>
      </div>

      {showEdit && (
        <RestaurantFormModal
          open={showEdit}
          onOpenChange={setShowEdit}
          editing={restaurant}
          onSaved={() => { setShowEdit(false); fetchData(); }}
        />
      )}

      {taskModal !== undefined && (
        <RestaurantTaskModal
          task={taskModal}
          restaurants={[{ id: restaurant.id, name: restaurant.name }]}
          users={users}
          lockedRestaurantId={restaurant.id}
          canEdit={canEdit}
          currentUser={user}
          onClose={() => setTaskModal(undefined)}
          onSaved={() => { setTaskModal(undefined); fetchData(); }}
        />
      )}
    </DashboardLayout>
  );
}
